import type { CSSProperties, ReactNode } from 'react';
import type { PictureTreatment } from '../../types/api';
import { TEXT_LIGHT, type TemplateBox } from './slideTemplates';
import {
  ARCH_HEIGHT,
  ARCH_WIDTH,
  CUTOUT_ANCHORED_HEIGHT,
  CUTOUT_BOX,
  FRAME_BORDER,
  FRAME_INSET,
  FRAME_MARGIN,
  FRAME_RADIUS,
  GRAIN_LIFT,
  HALFTONE_CELL,
  TOP_ARCH_WIDTH,
} from './slideTreatments';

interface TreatedPictureProps {
  /** Never `full_bleed`: that is the stage's ordinary background or slot. */
  treatment: Exclude<PictureTreatment, 'full_bleed'>;
  url: string;
  /** The picture box in slide fractions: the template's slot, or the whole canvas. */
  box: TemplateBox;
  /** True when the box is a slot template's image box rather than the canvas. */
  isSlot: boolean;
  /** Where the requested template anchors its caption; a full-canvas cut-out sits opposite. */
  captionAnchor: 'top' | 'center' | 'bottom';
  accent: string;
  panel: string;
}

/** A fraction of the slide as a container-width length; the stage is square. */
function cqw(fraction: number): string {
  return `${Math.round(fraction * 100000) / 1000}cqw`;
}

/** A centred box of the given size inside the picture box, in container units. */
function centredIn(box: TemplateBox, width: number, height: number): CSSProperties {
  return {
    left: cqw((box.width - width) / 2),
    top: cqw((box.height - height) / 2),
    width: cqw(width),
    height: cqw(height),
  };
}

/** A rounded-top arch of the given width, the renderer's `_arch_mask`. */
function archClip(width: number): string {
  const radius = cqw(width / 2);
  return `inset(0 round ${radius} ${radius} 0 0)`;
}

/**
 * One picture, presented the way its treatment asks, inside its picture box.
 *
 * An approximation like the rest of the stage (T9): the geometry of framed,
 * shaped and cut-out mirrors the renderer's constants, and grain, halftone
 * and tritone are CSS stand-ins for the Pillow passes. Everything happens
 * inside the box, so a slot template's treatment never reaches the panel
 * beside it. Decoration only: never a drag target.
 */
export function TreatedPicture({
  treatment,
  url,
  box,
  isSlot,
  captionAnchor,
  accent,
  panel,
}: TreatedPictureProps) {
  const picture = `url("${url}")`;

  function content(): ReactNode {
    switch (treatment) {
      case 'framed': {
        const inset = isSlot ? FRAME_INSET : FRAME_MARGIN;
        return (
          <span
            className={
              isSlot
                ? 'slide-canvas__treated-frame'
                : 'slide-canvas__treated-frame slide-canvas__treated-frame--lifted'
            }
            style={{
              inset: cqw(inset),
              backgroundImage: picture,
              borderRadius: cqw(FRAME_RADIUS),
              border: `${cqw(FRAME_BORDER)} solid ${accent}`,
            }}
          />
        );
      }
      case 'masked': {
        if (!isSlot) {
          // The poster arch: centred horizontally, standing on the bottom edge.
          return (
            <span
              className="slide-canvas__treated-fill"
              style={{
                inset: 'auto',
                left: cqw((1 - ARCH_WIDTH) / 2),
                top: cqw(1 - ARCH_HEIGHT),
                width: cqw(ARCH_WIDTH),
                height: cqw(ARCH_HEIGHT),
                backgroundImage: picture,
                clipPath: archClip(ARCH_WIDTH),
              }}
            />
          );
        }
        // A wide slot (image_top) takes an arch in the poster's proportions,
        // a tall one (image_left / image_right) takes a circle.
        const isWide = box.width > box.height;
        if (isWide) {
          const height = box.height - FRAME_INSET * 2;
          const width = TOP_ARCH_WIDTH;
          return (
            <span
              className="slide-canvas__treated-fill"
              style={{
                inset: 'auto',
                ...centredIn(box, width, height),
                backgroundImage: picture,
                clipPath: archClip(width),
              }}
            />
          );
        }
        const diameter = Math.min(box.width, box.height) - FRAME_INSET * 2;
        return (
          <span
            className="slide-canvas__treated-fill"
            style={{
              inset: 'auto',
              ...centredIn(box, diameter, diameter),
              backgroundImage: picture,
              clipPath: 'circle(50%)',
            }}
          />
        );
      }
      case 'cutout': {
        if (isSlot) {
          return (
            <span
              className="slide-canvas__treated-cutout"
              style={{ inset: cqw(FRAME_INSET), backgroundImage: picture }}
            />
          );
        }
        // Opposite the caption, as the renderer places it: under a lower-third
        // caption a shorter box up top, under an upper-third one the same box
        // low, otherwise the full square centred.
        const margin = (1 - CUTOUT_BOX) / 2;
        const anchoredHeight = CUTOUT_BOX * CUTOUT_ANCHORED_HEIGHT;
        const cutoutBox: CSSProperties =
          captionAnchor === 'center'
            ? centredIn(box, CUTOUT_BOX, CUTOUT_BOX)
            : {
                left: cqw(margin),
                top: cqw(captionAnchor === 'bottom' ? margin : 1 - margin - anchoredHeight),
                width: cqw(CUTOUT_BOX),
                height: cqw(anchoredHeight),
              };
        return (
          <>
            <span
              className="slide-canvas__treated-glow"
              style={{
                backgroundImage: `radial-gradient(circle, color-mix(in srgb, ${accent} 18%, transparent) 0%, transparent 65%)`,
              }}
            />
            <span
              className="slide-canvas__treated-cutout"
              style={{
                inset: 'auto',
                ...cutoutBox,
                backgroundImage: picture,
              }}
            />
          </>
        );
      }
      case 'grain':
        return (
          <>
            <span className="slide-canvas__treated-fill" style={{ backgroundImage: picture }} />
            <span className="slide-canvas__treated-grain" />
            <span
              className="slide-canvas__treated-lift"
              style={{ background: `rgb(${GRAIN_LIFT}, ${GRAIN_LIFT}, ${GRAIN_LIFT})` }}
            />
          </>
        );
      case 'halftone':
        return (
          <>
            <span
              className="slide-canvas__treated-fill slide-canvas__treated-fill--mono"
              style={{ backgroundImage: picture }}
            />
            <span
              className="slide-canvas__treated-tone slide-canvas__treated-tone--screen"
              style={{ background: panel }}
            />
            <span
              className="slide-canvas__treated-fill"
              style={{
                backgroundImage: `radial-gradient(circle at center, transparent 0 60%, ${TEXT_LIGHT} 66%)`,
                backgroundSize: `${cqw(HALFTONE_CELL)} ${cqw(HALFTONE_CELL)}`,
                backgroundRepeat: 'repeat',
                backgroundPosition: '0 0',
              }}
            />
          </>
        );
      case 'tritone':
        return (
          <>
            <span
              className="slide-canvas__treated-fill slide-canvas__treated-fill--mono"
              style={{ backgroundImage: picture }}
            />
            <span
              className="slide-canvas__treated-tone slide-canvas__treated-tone--color"
              style={{ background: accent }}
            />
            <span
              className="slide-canvas__treated-tone slide-canvas__treated-tone--lighten"
              style={{ background: panel }}
            />
          </>
        );
    }
  }

  return (
    <div
      className={`slide-canvas__treated slide-canvas__treated--${treatment}`}
      style={{
        left: `${box.left * 100}%`,
        top: `${box.top * 100}%`,
        width: `${box.width * 100}%`,
        height: `${box.height * 100}%`,
      }}
      aria-hidden="true"
    >
      {content()}
    </div>
  );
}
